<template lang="pug">
  .cost-center-role-dropdowns
    el-collapse-item(
      title="Role & Permission"
      :name="collapse"
    )
      template(slot="title")
        h3 {{ $t('cost_centers.roles_and_permissions') }}
        h4.ml-2 {{ form.cost_center_id ? costCenterName + ' (' + upperCaseFirstLetters(roleName) + ') ' : ''}}
      .role-and-permissions
      el-form(
        :model="form"
        :rules="rolesAndPermissionsRules"
        ref="RolesAndPermissionsForm"
        @submit.native.prevent
      )
        el-row(:gutter="25")
          el-col(:span="8")
            el-form-item(
              prop="cost_center_id"
              :label="$t('common.cost_center')"
            )
              el-select.w-100(
                v-model='form.cost_center_id'
                :disabled='superAdmin ? superAdmin : isSelectedCostCenterDisabled'
                filterable
                :placeholder="$t('placeholder.select_cost_center')"
                @change="handleOnDropdownSelection($event, collapse, 'costCenter')"
              )
                el-option(
                  v-for='cost_center in loadedCostCenters'
                  :key='cost_center.id'
                  :label='cost_center.name'
                  :value='cost_center.id'
                  :disabled="cost_center.disabled"
                )
          el-col(:span="8")
            el-form-item(
              prop="role_id"
              :label="$t('label.role')"
            )
              //- :disabled='superAdmin ? superAdmin : isSelectedCostCenterDisabled'
              el-select.w-100(
                v-model='form.role_id'
                filterable
                :disabled='isSelectedCostCenterDisabled'
                :placeholder="$t('common.select_role')"
                @change="handleOnDropdownSelection($event, collapse, 'role')"
              )
                el-option(
                  v-for='role in roles'
                  :key='role.id'
                  :disabled='role.name == "administrator"'
                  :label='upperCaseFirstLetters(role.name)'
                  :value='role.id'
                )
        RolePermissionsList(
          v-model="checkList"
          v-if="!superAdmin"
          :isDisabled='superAdmin ? superAdmin : isSelectedCostCenterDisabled'
          :isEdit="isEdit"
          :newRoleSelect="newRoleSelect"
          :role="{permissions: loadedChecklists}"
          :collapse="collapse"
          :key="rolePermissionsListKey"
        )
    .d-flex.align-items-center.justify-content-flex-end
      el-button.mb-4.dark-blue-btn.pop-up-btn.delete-btn(
        v-if="collapses.length > 1"
        @click="handleRemoveCollapse(index)"
      ) {{ $t('common.remove') }}
</template>

<script>
import _ from "lodash";
import { usersViewValidations } from "@utils/formValidations/usersViewValidations";
import { customValidations } from "@utils/customValidators";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { upperCaseFirstLetters } from "@utils/generalUtils";
import RolePermissionsList from "@components/_shared/RolePermissionsList";
import { roles } from '@utils/roles'

export default {
  name: "CollapseComponent",
  components: { RolePermissionsList },
  mixins: [usersViewValidations, customValidations],

  props: {
    isEdit: {
      type: Boolean,
      required: false
    },

    collapses: {
      type: Array,
      default: 0,
    },

    collapse: {
      type: Number,
      default: null,
    },

    customerCollapse: {
      type: Number,
      default: null,
    },

    index: {
      type: Number,
      default: 0,
    },

    superAdmin: {
      type: Boolean,
      required: false
    },

    userDetails: {
      type: Object,
      required: false
    }
  },

  computed: {
    ...mapState("user", ["loadedCostCenters"]),
    ...mapGetters("user", ["getCollapsesData"]),

    isAdminRole(){
      return [roles.SUPER_ADMINISTRATOR, roles.ADMINISTRATOR].some(
              (r) => this.userDetails.roles.map((role) => role.name).indexOf(r) >= 0
      )
    },

    disableAdminRole(){
      if(this.roles.find(role => role.name === roles.ADMINISTRATOR)) 
      // return true;
        return Number(this.roles.find(role => role.name === roles.ADMINISTRATOR).id);
      return 0;
    },

    roleName(){
      let roleName = this.roles.filter(role => this.form.role_id == role.id).map(role => role.name);

      return roleName.toString();
    },

    costCenterName(){
      let costCenterName = this.loadedCostCenters.find(costCenterName => this.form.cost_center_id
          .includes(costCenterName.id))
          // .map(costCenterName => costCenterName.name);

      return costCenterName.name.toString();
    },

    isSelectedCostCenterDisabled() {
      // if the role administrator or super administrator is selected then
      // disable and clear the cost center field
      let rolesNamesFromIds = this.roles.filter(role => this.form.role_id == role.id).map(role => role.name)

      return rolesNamesFromIds.some((val) => [roles.SUPER_ADMINISTRATOR, roles.ADMINISTRATOR].includes(val))
    }
  },

  watch: {
    getCollapsesData() {
      this.getCostCenterData();
    },

    checkList() {
      this.handleUpdateCollapseData();
      this.handleUpdateUserCostCenterPermissions(this.checkList);
    },
  },

  data() {
    return {
      checkList: [],
      prevDisabledCostCenterId: null,
      roles: [],
      loadedChecklists: [],
      mappedRestrictedOrAuthorizedPermissions: [],
      form: {
        cost_center_id: null,
        role_id: null,
      },
      loading: false,
      newRoleSelect: false,
      rolePermissionsListKey: 0,
      upperCaseFirstLetters,
    };
  },

  async created() {
    await this.getCostCenterData()
    await this.handleGetRoles();
  },

  methods: {
    ...mapActions("costCenter", ["getCostCenters"]),
    ...mapActions("role", [
      "getRoles",
      "getRole",
      "getAllPermissionsByRoleIds",
    ]),
    ...mapMutations("user", [
      "setCollapsesData",
      "removeCollapseData",
      "setDisabledCostCenter",
    ]),

    async getCostCenterData() {
      let foundCollapseDataIndex = this.getCollapsesData.find(
          (data) => data.collapse === this.collapse
      );
      let foundCollapseData = this.getCollapsesData[this.index];

      if (foundCollapseData) {
        this.form.cost_center_id = foundCollapseData.cost_center_id;
        this.form.role_id = foundCollapseData.role_id;
        this.checkList = _.clone(foundCollapseData.alreadyChecked); // vuex mutation error fix
        this.loadedChecklists = foundCollapseData.loadedChecklists;
        this.prevDisabledCostCenterId = foundCollapseData.cost_center_id
      }
    },

    async handleRemoveCollapse(index) {
      if (this.collapses.length > 1) {
        this.$emit("removeCollapse", index);
        this.removeCollapseData(this.collapse); // remove this selected collapse
        this.setDisabledCostCenter({
          selectedCostCenterId: this.form.cost_center_id,
          disabled: false,
        });
      }
    },

    async handleGetRoles() {
      // if(this.superAdmin)
      //   this.roles= this.userDetails.roles;

      this.roles = await this.getRoles();
    },

    async loadCheckedPermissions(roleId) {
      this.checkList = [];
      this.loadedChecklists = [];
      let params = `?by_role_ids=${roleId}`;

      if (roleId.length > 0) {
        this.loadedChecklists = await this.getAllPermissionsByRoleIds(params);
        this.rolePermissionsListKey += 1
      }
    },

    handleUpdateUserCostCenterPermissions(checkList) {
      if (this.loadedChecklists) {
        const removedPermissions = _.difference(
            this.loadedChecklists.map((perm) => perm.id),
            checkList
        );
        const addedPermissions = _.difference(
            checkList,
            this.loadedChecklists.map((perm) => perm.id)
        );
        this.mappedRestrictedOrAuthorizedPermissions = [];

        const computeRestrictedOrAuthorizedPermission = (grant_type, permId) => {
          return {
            cost_center_id: this.form.cost_center_id,
            permission_id: permId,
            grant_type,
            collapse: this.collapse,
          };
        };

        // map and add to user_cost_center_permissions
        removedPermissions.forEach((removedPermId) => {
          this.mappedRestrictedOrAuthorizedPermissions.push(
              computeRestrictedOrAuthorizedPermission("restrict", removedPermId)
          );
        });

        addedPermissions.forEach((addedPermId) => {
          this.mappedRestrictedOrAuthorizedPermissions.push(
              computeRestrictedOrAuthorizedPermission("authorize", addedPermId)
          );
        });

        this.setCollapsesData({
          collapse: this.collapse,
          cost_center_id: this.isSelectedCostCenterDisabled ? null : this.form.cost_center_id,
          role_id: this.form.role_id,
          alreadyChecked: this.checkList,
          loadedChecklists: this.loadedChecklists,
          permissions: this.mappedRestrictedOrAuthorizedPermissions,
        });
      }
    },

    async handleOnDropdownSelection(selectedCostCenterId, collapse, dropdown) {
      this.handleUpdateCollapseData();
      // load role from dropdown
      // handleRemoveCollapse
      if (dropdown === "role") {
        if (collapse !== undefined) {
          this.newRoleSelect = true
          await this.loadCheckedPermissions(this.form.role_id);
        }
      }

      if (dropdown === "costCenter") {
        this.handleUpdateUserCostCenterPermissions(this.checkList);
        if (
            this.prevDisabledCostCenterId &&
            this.prevDisabledCostCenterId !== selectedCostCenterId
        ) {
          this.setDisabledCostCenter({
            selectedCostCenterId: this.prevDisabledCostCenterId,
            disabled: false,
          });
        }
        this.prevDisabledCostCenterId = selectedCostCenterId;
        this.setDisabledCostCenter({
          selectedCostCenterId,
          disabled: true,
        });
      }
    },

    handleUpdateCollapseData() {
      this.setCollapsesData({
        collapse: this.collapse,
        cost_center_id: this.isSelectedCostCenterDisabled ? null : this.form.cost_center_id, // send null if cost center is disabled
        role_id: this.form.role_id,
        alreadyChecked: this.checkList,
        loadedChecklists: this.loadedChecklists,
        permissions: [],
      });
    },
  },
};
</script>
